import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation, Trans } from 'react-i18next';
import Dropzone from '../dropzone';
import { sameNamesCheck, decorateWithId } from '../utils';
import iconCamera from '../../../assets/svg/camera-icon-rounded.svg';
import iconComputer from '../../../assets/svg/computer-icon-rounded.svg';
import DocumentUploaderSC from './document-uploader.styled';
import CameraModal from './components/camera-modal';
import FileDisplay from './components/file-display';
import SvgIcon from '../../svg-icon';
import ErrorList from './components/error-list';
import ReactTooltip from 'react-tooltip';

class DocumentUploader extends React.PureComponent {
  acceptedFileTypes = ['image/jpeg', 'image/png', 'application/pdf'];

  // TODO ENV VAR
  maxFileSize = 8306688;

  constructor(props) {
    super(props);
    this.fileUploadRef = React.createRef();
    this.state = {
      disabled: props.disabled,
      isCameraModalOpen: false,
      internalErrors: []
    };
  }

  getDefaultDropZoneProps = () => {
    const { t } = this.props;
    return {
      renderFiles: true,
      files: [],
      onChange: files => this.handleAddFiles(files),
      errors: [],
      onError: this.setValidationErrors,
      accept: this.acceptedFileTypes.join(', '),
      genericErrorMsg: t('documentUploader.genericErrorMsg'),
      maxSize: this.maxFileSize,
      maxFiles: 1
    };
  };

  handleDeleteFileClick = id => {
    const { value } = this.props;
    const filteredFiles = value.filter(item => item.id !== id);

    this.setValidationErrors();
    this.setFilesToValidate(filteredFiles);
  };

  handleAddFiles = inputFiles => {
    // type input files when needed
    this.setValidationErrors();
    const newFiles =
      inputFiles instanceof Array ? inputFiles : [...inputFiles.target.files];

    newFiles.forEach(item => this.processNewFile(item));
  };

  setValidationErrors = (newErrors = []) => {
    const { onError, onTouch } = this.props;

    onTouch(true);
    onError(newErrors);

    this.setState(state => ({
      ...state,
      internalErrors: newErrors
    }));
  };

  setFilesToValidate = newFiles => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(newFiles);
    }
  };

  // eslint-disable-next-line consistent-return
  processNewFile = newFile => {
    const { value, t } = this.props;
    const newErrors = [];

    // check if there is file with name that already exists in files array
    if (sameNamesCheck(newFile, value)) {
      newErrors.push(t('documentUploader.nameError'));
    }
    // test again accept file format
    if (this.acceptedFileTypes.indexOf(newFile.type) === -1) {
      newErrors.push(t('documentUploader.formatError'));
    }
    // test again file size
    if (newFile.size > this.maxFileSize) {
      newErrors.push(t('documentUploader.sizeError'));
    }

    this.setValidationErrors(newErrors);

    if (newErrors.length > 0) {
      return null;
    }

    // decorate files with id
    const decoratedFile = decorateWithId(newFile);
    this.setFilesToValidate([...value, decoratedFile]);
  };

  handleUseCameraClick = () => {
    this.setTouched();
    this.setState(state => ({
      ...state,
      isCameraModalOpen: true
    }));
  };

  handleCloseModalClick = () => {
    this.setState(state => ({
      ...state,
      isCameraModalOpen: false
    }));
  };

  handleBrowseClick = () => {
    this.fileUploadRef.current.click();
  };

  setTouched = (value = true) => {
    const { onTouch } = this.props;

    if (onTouch) {
      onTouch(value);
    }
  };

  // Prioritize internal messages over global ones
  renderErrors = () => {
    const { errors, touched } = this.props;
    const { internalErrors } = this.state;

    const result = [];

    if (touched) {
      if (internalErrors.length > 0) {
        result.push(<ErrorList errors={internalErrors} />);
      } else {
        result.push(<ErrorList errors={errors} />);
      }
    }
    return result;
  };

  render() {
    const {
      allowMultipleFiles,
      devices,
      responsiveContentView,
      label,
      subLabel,
      textAddition,
      maxFiles,
      value,
      errors,
      touched,
      t
    } = this.props;
    const { isCameraModalOpen, internalErrors } = this.state;

    const dropzoneProps = this.getDefaultDropZoneProps();
    dropzoneProps.files = value;
    dropzoneProps.maxFiles = maxFiles;

    return !responsiveContentView ? (
      <DocumentUploaderSC>
        <h5>{label}</h5>
        <h6>{subLabel}</h6>
        <p className="upload-info-text">{textAddition}</p>
        <p className="upload-info-text">
          <Trans i18nKey="documentUploader.infoText" ns="components" />
        </p>
        {this.renderErrors()}
        <FileDisplay files={value} onDelete={this.handleDeleteFileClick} />
        <div className="first-row">
          <div className="from-computer">
            <div className="left-side">
              <SvgIcon icon={iconComputer} alt="computer_icon" themed active />
            </div>
            <div className="right-side">
              <span>{t('documentUploader.uploadText')}</span>
              <button
                type="button"
                onClick={this.handleBrowseClick}
                className="btn btn-secondary"
                disabled={value.length >= maxFiles}
              >
                {t('documentUploader.browseButton')}
              </button>
              <input
                type="file"
                style={{ display: 'none' }}
                ref={this.fileUploadRef}
                multiple={allowMultipleFiles ? 'multiple' : false}
                onChange={this.handleAddFiles}
              />
            </div>
          </div>
        </div>
        <div className="second-row">
          <Dropzone
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...dropzoneProps}
          />
        </div>
        <CameraModal
          isCameraModalOpen={isCameraModalOpen}
          responsiveContentView={responsiveContentView}
          onAdd={this.handleAddFiles}
          onClose={this.handleCloseModalClick}
        />
      </DocumentUploaderSC>
    ) : (
      // responsive view
      <DocumentUploaderSC>
        <h5>{label}</h5>
        <h6>{subLabel}</h6>
        {touched && <ErrorList errors={internalErrors} />}
        {touched && <ErrorList errors={errors} />}
        <FileDisplay files={value} onDelete={this.handleDeleteFileClick} />
        <div className="upload-choice">
          <button
            type="button"
            className="from-computer centered"
            onClick={this.handleBrowseClick}
            disabled={value.length >= maxFiles}
            stylig="primary"
          >
            <SvgIcon
              icon={iconComputer}
              alt="computer_icon"
              active={!(value.length >= maxFiles)}
              themed
            />
            <p>{t('documentUploader.addButon')}</p>
            <input
              type="file"
              style={{ display: 'none' }}
              ref={this.fileUploadRef}
              multiple={allowMultipleFiles ? 'multiple' : false}
              onChange={this.handleAddFiles}
            />
          </button>
          {devices.camera.inputs.length > 0 && (
            <div
              className="camera-button-container"
              data-tip
              data-for="cameraDisabledTipMobile"
            >
              <button
                type="button"
                className="from-camera centered"
                onClick={this.handleUseCameraClick}
                disabled
              >
                <SvgIcon
                  icon={iconCamera}
                  alt="camera_icon"
                  themed
                  active={
                    !(
                      value.length >= maxFiles ||
                      devices.camera.inputs.length < 1
                    )
                  }
                />
                <p>{t('documentUploader.cameraButton')}</p>
              </button>
              <ReactTooltip
                id="cameraDisabledTipMobile"
                place="top"
                effect="solid"
              >
                {t('documentUploader.cameraBtnDisabledTooltip')}
              </ReactTooltip>
            </div>
          )}
        </div>
        <CameraModal
          isCameraModalOpen={isCameraModalOpen}
          responsiveContentView={responsiveContentView}
          onAdd={this.handleAddFiles}
          onClose={this.handleCloseModalClick}
        />
      </DocumentUploaderSC>
    );
  }
}

DocumentUploader.propTypes = {
  allowMultipleFiles: PropTypes.bool,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  minSize: PropTypes.number,
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  accept: PropTypes.string,
  genericErrorMsg: PropTypes.string,
  responsiveContentView: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.string.isRequired,
  textAddition: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func,
  onTouch: PropTypes.func,
  onError: PropTypes.func,
  t: PropTypes.func.isRequired,
  devices: PropTypes.shape({
    camera: PropTypes.shape({
      inputs: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired,
  errors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string
  ]),
  touched: PropTypes.bool
};

DocumentUploader.defaultProps = {
  description: null,
  disabled: false,
  minSize: 0,
  maxSize: Infinity,
  maxFiles: 1,
  accept: null,
  genericErrorMsg: null,
  textAddition: '',
  onChange: () => {},
  onTouch: () => {},
  onError: () => {},
  touched: false,
  errors: []
};

export default withTranslation('components')(
  connect(state => ({
    devices: state.devices,
    responsiveContentView: state.ui.responsiveContent
  }))(DocumentUploader)
);
